import type { PdsServerStage } from '../gateway';
import type { PdsChannel, PdsOrder, PdsOrderItem, PdsStage } from '../types';
import type { PdsApiOrder, PdsApiOrderItem } from './types';

const STAGE_BY_STATUS: Record<string, PdsStage> = {
  pending: 'new',
  in_progress: 'preparing',
  ready: 'ready',
  served: 'handed',
  completed: 'handed',
  terminated: 'handed',
  rejected: 'refused',
  cancelled: 'cancelled',
  failled: 'cancelled',
};

export const STATUS_BY_STAGE: Record<PdsServerStage, string> = {
  preparing: 'in_progress',
  ready: 'ready',
  handed: 'served',
  refused: 'rejected',
};

const CHANNEL_LABEL: Record<string, PdsChannel> = {
  click_and_collect: 'Click & Collect',
  pos_register: 'Sur place',
};

export const stageFromStatus = (status: string): PdsStage =>
  STAGE_BY_STATUS[status] ?? 'cancelled';

// Une commande en ligne non payée n'est pas encore une commande : le PDS l'ignore jusqu'au paiement.
export const isVisibleOnBoard = (order: PdsApiOrder) =>
  order.status !== 'pending' ||
  order.paymentStatus === 'paid' ||
  order.paymentMethod === 'cash';

const sameText = (a?: string | null, b?: string | null) =>
  (a ?? '').trim().toLowerCase() === (b ?? '').trim().toLowerCase();

// Un produit à variante unique porte la désignation du produit comme libellé : l'afficher en option répète le titre.
const optionLine = (item: PdsApiOrderItem) =>
  [
    sameText(item.productVariant?.label, item.product?.designation)
      ? undefined
      : item.productVariant?.label,
    ...(item.extras ?? []).flatMap((extra) =>
      (extra.options ?? []).map(({ qty = 1, option }) =>
        option?.content ? `${qty > 1 ? `${qty}× ` : ''}${option.content}` : '',
      ),
    ),
    item.clientNote,
  ]
    .filter(Boolean)
    .join(' · ');

const toItem = (item: PdsApiOrderItem): PdsOrderItem => ({
  id: item.id,
  quantity: item.qty,
  name: item.product?.designation ?? 'Produit',
  option: optionLine(item) || undefined,
  prepared: item.preparationStatus === 'ready',
  removedAt: item.removedAt ? Date.parse(item.removedAt) : undefined,
  unitPriceCents:
    item.unityPriceInclTax === undefined
      ? undefined
      : Math.round(item.unityPriceInclTax * 100),
});

// « R01-2610-000114 » : seul le rang compte à l'écran, le préfixe est celui du commerce.
const sequenceOf = (orderNumber?: string | null) => {
  const match = /(\d+)$/.exec(orderNumber ?? '');
  return match ? Number(match[1]) : 0;
};

export const toPdsOrder = (order: PdsApiOrder): PdsOrder => {
  const stage = stageFromStatus(order.status);
  const lines = order.items ?? [];
  const updatedAt = order.updatedAt ? Date.parse(order.updatedAt) : undefined;

  return {
    id: order.id,
    number: sequenceOf(order.orderNumber),
    orderNumber: order.orderNumber || undefined,
    label: order.customerName || order.phone || 'Commande',
    contact: {
      name: order.customerName || undefined,
      phone: order.phone || undefined,
      email: order.email || undefined,
    },
    channel: CHANNEL_LABEL[order.channel ?? ''] ?? 'Click & Collect',
    pickupCode: (order.pickupCode ?? '').split('').join(' '),
    dueAt: order.pickupTime ? Date.parse(order.pickupTime) : Date.now(),
    stage,
    customerNote: order.clientNote || undefined,
    items: lines.filter((item) => !item.removedAt).map(toItem),
    removedItems: lines.filter((item) => item.removedAt).map(toItem),
    totalPaidCents: Math.round((order.totalPrice ?? 0) * 100),
    readyAt: stage === 'ready' ? updatedAt : undefined,
    handedAt: stage === 'handed' ? updatedAt : undefined,
    cancelledAt: stage === 'cancelled' ? updatedAt : undefined,
  };
};
